import * as React from 'react';

import { IBuilderSuggest } from '../../../types/builder';
import { BuilderSuggestions } from './BuilderSuggestions';

import { VillageSuggestions } from './VillageSuggestions';
import { StationsSuggestions } from './StationsSuggestions';
import { AddressSuggestions } from './AddressSuggestions';
import { RoadsSuggestions } from './RoadsSuggestions';

import * as styles from './index.css';
import { IGeoSuggestItem } from '../../../utils/geo';
import { IRailwaySuggest } from '../../../store/stations';
import { IRoadSuggest } from '../../../store/roads';
import { IVillageSuggest } from '../../../types/village';
import { IRailwayDirection } from '../../../types/geo';
import { isArrayWithItems } from '../../../utils';
import { isVillageQuery } from '../../../utils/filters_helpers';

interface ISuggestionsProps {
  textValue: string;
  isLoading: boolean;
  isResultsVisible: boolean;

  villageSuggests?: IVillageSuggest[];
  roadsSuggests?: IRoadSuggest[];
  stationsDirections: IRailwayDirection[];
  stationsSuggests?: IRailwaySuggest[];
  addressSuggests?: IGeoSuggestItem[];
  builderSuggests?: IBuilderSuggest[];

  handleRoadItemSelect(suggest: IRoadSuggest): void;
  handleStationItemSelect(suggest: IRailwaySuggest): void;
  handleAddressItemSelect(suggest: IGeoSuggestItem): void;
  handleBuilderItemSelect(suggest: IBuilderSuggest): void;
}

export const Suggestions: React.FC<ISuggestionsProps> = props => {
  const {
    textValue,
    isLoading,
    isResultsVisible,
    villageSuggests,
    roadsSuggests,
    stationsDirections,
    stationsSuggests,
    addressSuggests,
    builderSuggests,
    handleRoadItemSelect,
    handleStationItemSelect,
    handleAddressItemSelect,
    handleBuilderItemSelect,
  } = props;

  const isVillageQueryString = isVillageQuery(textValue);

  const hasVillageSuggestions = isArrayWithItems(villageSuggests);
  const hasRoadsSuggestions = !isVillageQueryString && isArrayWithItems(roadsSuggests);
  const hasStationsSuggestions = isArrayWithItems(stationsSuggests) && !isVillageQueryString;
  const hasAddressSuggestions = isArrayWithItems(addressSuggests);
  const hasBuilderSuggestions = isArrayWithItems(builderSuggests);

  const hasSuggestions =
    hasVillageSuggestions ||
    hasRoadsSuggestions ||
    hasStationsSuggestions ||
    hasAddressSuggestions ||
    hasBuilderSuggestions;

  if (isLoading || !isResultsVisible || !hasSuggestions) {
    return null;
  }

  return (
    <div className={styles['list-container']}>
      {hasVillageSuggestions && (
        <VillageSuggestions textValue={textValue} suggests={villageSuggests as IVillageSuggest[]} />
      )}
      {hasRoadsSuggestions && (
        <RoadsSuggestions
          textValue={textValue}
          suggests={roadsSuggests as IRoadSuggest[]}
          handleItemSelect={handleRoadItemSelect}
        />
      )}
      {hasStationsSuggestions && (
        <StationsSuggestions
          textValue={textValue}
          directions={stationsDirections}
          suggests={stationsSuggests as IRailwaySuggest[]}
          handleItemSelect={handleStationItemSelect}
        />
      )}
      {hasAddressSuggestions && (
        <AddressSuggestions
          textValue={textValue}
          suggests={addressSuggests as IGeoSuggestItem[]}
          handleItemSelect={handleAddressItemSelect}
        />
      )}
      {hasBuilderSuggestions && (
        <BuilderSuggestions
          textValue={textValue}
          suggests={builderSuggests as IBuilderSuggest[]}
          handleItemSelect={handleBuilderItemSelect}
        />
      )}
    </div>
  );
};
